import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ReportType =
  | 'GAP_MATRIX'
  | 'GAP_TIMESERIES'
  | 'NIM'
  | 'EVE'
  | 'DURATION'
  | 'LCR'
  | 'NSFR'
  | 'FTP'
  | 'STRESS'
  | 'BAM';

export type DateMode   = 'LAST_EOD' | 'SPECIFIC';
export type TsDateMode = 'LAST_7D' | 'LAST_30D' | 'LAST_90D' | 'SPECIFIC';
export type TriggerBy  = 'MANUAL' | 'SCHEDULED';
export type SnapshotStatus = 'RUNNING' | 'SUCCESS' | 'FAILED';

export interface GapMatrixParams {
  dateMode: DateMode;
  specificDate?: string | null;
  // Kept for legacy/standalone use when gapConfigId is absent.
  // When gapConfigId is set, the backend resolves these from GapConfiguration.
  rowDimension?: string | null;
  segmentationId?: string | null;
  currency?: string | null;
}

export interface NimParams {
  currency?: string | null;
  dateMode: DateMode;
  specificDate?: string | null;
  horizonMonths?: number;
  scenarioCode?: string | null;
}

export interface GapTimeSeriesParams {
  gapType?: 'LIQUIDITY' | 'INTEREST_RATE';
  dateMode: TsDateMode;
  specificFrom?: string | null;
  specificTo?: string | null;
  mode?: 'FLOW' | 'PRINCIPAL';
}

export interface EveParams {
  dateMode: DateMode;
  specificDate?: string | null;
  scenarioSet?: string | null;
}

export interface DateOnlyParams {
  dateMode: DateMode;
  specificDate?: string | null;
}

export interface StressParams {
  dateMode: DateMode;
  specificDate?: string | null;
  stressScenarioId?: string | null;
}

export type ReportParams =
  | GapMatrixParams
  | NimParams
  | GapTimeSeriesParams
  | EveParams
  | DateOnlyParams
  | StressParams;

export interface ReportConfig {
  id: string;
  name: string;
  description?: string;
  reportType: ReportType;
  gapConfigId?: string | null;
  isPreset: boolean;
  parameters: ReportParams;
  isScheduled: boolean;
  cronExpression?: string | null;
  scheduleActive: boolean;
  lastRunAt?: string | null;
  nextRunAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ReportSnapshot {
  id: string;
  reportConfigId: string;
  triggeredBy: TriggerBy;
  triggeredAt: string;
  asOfDate: string;
  status: SnapshotStatus;
  errorMessage?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: Record<string, any> | null;
  durationMs?: number | null;
  completedAt?: string | null;
}

export type ReportConfigCreate = Omit<ReportConfig,
  'id' | 'isPreset' | 'lastRunAt' | 'nextRunAt' | 'createdAt' | 'updatedAt'>;
export type ReportConfigUpdate = ReportConfigCreate & { id: string };

export const TYPE_LABELS: Record<ReportType, string> = {
  GAP_MATRIX:     'Gap Matrix',
  GAP_TIMESERIES: 'Gap Time Series',
  NIM:            'NIM',
  EVE:            'EVE / KRD',
  DURATION:       'Duration / DV01',
  LCR:            'LCR',
  NSFR:           'NSFR',
  FTP:            'FTP',
  STRESS:         'Stress test',
  BAM:            'Rapport BAM',
};

export const GAP_BASED_TYPES: ReportType[] = [
  'GAP_MATRIX', 'GAP_TIMESERIES', 'EVE', 'DURATION',
];

const KEYS = {
  all:       ()           => ['reportConfigs'] as const,
  one:       (id: string) => ['reportConfigs', id] as const,
  snapshots: (id: string) => ['reportConfigs', id, 'snapshots'] as const,
};

const BASE = (...parts: string[]) => apiPath('alm', 'report-configs', ...parts);

export function useReportConfigs(
  options?: Omit<UseQueryOptions<ReportConfig[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn:  () => apiGet<ReportConfig[]>(BASE()),
    ...options,
  });
}

export function useReportConfig(
  id: string | undefined,
  options?: Omit<UseQueryOptions<ReportConfig, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.one(id!),
    enabled:  Boolean(id),
    queryFn:  () => apiGet<ReportConfig>(BASE(id!)),
    ...options,
  });
}

export function useCreateReportConfig() {
  const qc = useQueryClient();
  return useMutation<ReportConfig, CubixApiError, ReportConfigCreate>({
    mutationFn: (body) => apiPost<ReportConfig>(BASE(), body),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}

export function useUpdateReportConfig() {
  const qc = useQueryClient();
  return useMutation<ReportConfig, CubixApiError, ReportConfigUpdate>({
    mutationFn: ({ id, ...body }) => apiPut<ReportConfig>(BASE(id), body),
    onSuccess:  (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all() });
      qc.invalidateQueries({ queryKey: KEYS.one(vars.id) });
    },
  });
}

export function useDeleteReportConfig() {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, string>({
    mutationFn: (id) => apiDelete(BASE(id)),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}

export function useRunReportConfig() {
  const qc = useQueryClient();
  return useMutation<ReportSnapshot, CubixApiError, string>({
    mutationFn: (id) => apiPost<ReportSnapshot>(BASE(id, 'run'), {}),
    onSuccess:  (_, id) => {
      qc.invalidateQueries({ queryKey: KEYS.snapshots(id) });
      qc.invalidateQueries({ queryKey: KEYS.all() });
    },
  });
}

export function useReportSnapshots(
  configId: string | undefined,
  options?: Omit<UseQueryOptions<ReportSnapshot[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.snapshots(configId!),
    enabled:  Boolean(configId),
    queryFn:  () => apiGet<ReportSnapshot[]>(BASE(configId!, 'snapshots')),
    ...options,
  });
}
